import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  addLocalDays,
  localDateString,
  saveRestaurantDemoState,
  type RestaurantDemoProduct,
  type RestaurantDemoState,
} from "../sales-playground/restaurant-sandbox";
import {
  modifierGroupsForProduct,
  visualForProduct,
  type RestaurantDemoModifierGroup,
  type RestaurantDemoV5State,
} from "../sales-playground/restaurant-demo-v5-state";
import {
  addRestaurantCustomerCartLine,
  buildRestaurantCustomerCartLine,
  changeRestaurantCustomerCartQuantity,
  commitRestaurantCustomerOrder,
  commitRestaurantCustomerReservation,
  restaurantCustomerCartTotalMinor,
  type RestaurantCustomerCartLine,
  type RestaurantCustomerFulfillment,
} from "../sales-playground/restaurant-customer-ordering";
import {
  restaurantCommercialAdminPath,
  type RestaurantCommercialCustomerSurface,
} from "./restaurant-commercial-routing";
import { ensureRestaurantCommercialCatalog, ensureRestaurantCommercialV5 } from "./restaurant-commercial-catalog";

const money = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 });
const formatMoney = (minor: number) => money.format(minor / 100);

type MenuMode = "menu" | "order" | "reservation";

const FALLBACK_IMAGES: Readonly<Record<string, string>> = Object.freeze({
  Entradas: "https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1000&q=82",
  Pizzas: "https://images.unsplash.com/photo-1574071318508-1cdbab80d002?auto=format&fit=crop&w=1000&q=82",
  Principales: "https://images.unsplash.com/photo-1546833999-b9f581a1996d?auto=format&fit=crop&w=1000&q=82",
  Pastas: "https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&w=1000&q=82",
  Postres: "https://images.unsplash.com/photo-1551024506-0bccd828d307?auto=format&fit=crop&w=1000&q=82",
  Bebidas: "https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&w=1000&q=82",
  Cafetería: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1000&q=82",
});

function normalized(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("es").trim();
}

function intentKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID().replaceAll("-", "");
  return `${Date.now()}${Math.random().toString(36).slice(2)}`;
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "CN";
}

function defaultSelections(groups: readonly RestaurantDemoModifierGroup[]): Readonly<Record<string, readonly string[]>> {
  const selections: Record<string, readonly string[]> = {};
  for (const group of groups) selections[group.id] = group.minSelections > 0 ? Object.freeze(group.options.slice(0, group.minSelections).map((option) => option.id)) : Object.freeze([]);
  return Object.freeze(selections);
}

function configuredPrice(
  product: RestaurantDemoProduct,
  groups: readonly RestaurantDemoModifierGroup[],
  selections: Readonly<Record<string, readonly string[]>>,
): number {
  return product.priceMinor + groups.reduce((sum, group) => sum + (selections[group.id] ?? []).reduce((inner, optionId) => inner + (group.options.find((option) => option.id === optionId)?.priceDeltaMinor ?? 0), 0), 0);
}

function Header({ businessSlug, businessName, onMenu, navigate }: {
  businessSlug: string;
  businessName: string;
  onMenu: () => void;
  navigate: (path: string) => void;
}) {
  return <header className="rcd-public-header rcd-landing-header">
    <button className="rcd-brand" type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
      <span>{initials(businessName)}</span><div><strong>{businessName}</strong><small>Cocina & encuentros</small></div>
    </button>
    <nav aria-label="Navegación del restaurante"><button type="button" onClick={onMenu}>Menú</button></nav>
    <button className="rcd-secondary rcd-admin-link" type="button" onClick={() => navigate(restaurantCommercialAdminPath(businessSlug))}>Administración ↗</button>
  </header>;
}

function ProductVisual({ product, v5 }: { product: RestaurantDemoProduct; v5: RestaurantDemoV5State }) {
  const visual = visualForProduct(v5, product);
  const src = visual.imageUrl || FALLBACK_IMAGES[product.category] || FALLBACK_IMAGES.Principales;
  return <div className="rcd-product-visual rcd-real-product-visual"><img src={src} alt={product.name} loading="lazy" /></div>;
}

function ProductCard({ product, v5, actionLabel, onAction }: {
  product: RestaurantDemoProduct;
  v5: RestaurantDemoV5State;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const groups = modifierGroupsForProduct(v5, product.id);
  const minimum = configuredPrice(product, groups, defaultSelections(groups));
  return <article className="rcd-product-card rcd-landing-product-card">
    <ProductVisual product={product} v5={v5} />
    <div className="rcd-product-body">
      <div className="rcd-product-meta"><span>{product.category}</span>{!product.available ? <span className="paused">No disponible</span> : null}</div>
      <h3>{product.name}</h3>
      <p>{product.description}</p>
      <footer><strong>{groups.length ? `Desde ${formatMoney(minimum)}` : formatMoney(product.priceMinor)}</strong>{actionLabel && onAction ? <button type="button" disabled={!product.available} onClick={onAction}>{product.available ? actionLabel : "Agotado"}</button> : null}</footer>
    </div>
  </article>;
}

function Configurator({ product, state, v5, onClose, onAdd }: {
  product: RestaurantDemoProduct;
  state: RestaurantDemoState;
  v5: RestaurantDemoV5State;
  onClose: () => void;
  onAdd: (line: RestaurantCustomerCartLine) => void;
}) {
  const groups = modifierGroupsForProduct(v5, product.id);
  const [selections, setSelections] = useState<Readonly<Record<string, readonly string[]>>>(() => defaultSelections(groups));
  const total = configuredPrice(product, groups, selections);
  const valid = groups.every((group) => (selections[group.id]?.length ?? 0) >= group.minSelections && (selections[group.id]?.length ?? 0) <= group.maxSelections);

  function toggle(group: RestaurantDemoModifierGroup, optionId: string) {
    const selected = selections[group.id] ?? [];
    const active = selected.includes(optionId);
    let next: readonly string[];
    if (active) next = selected.filter((id) => id !== optionId);
    else if (group.maxSelections === 1) next = [optionId];
    else next = [...selected, optionId].slice(0, group.maxSelections);
    setSelections(Object.freeze({ ...selections, [group.id]: Object.freeze(next) }));
  }

  function add() {
    const result = buildRestaurantCustomerCartLine({ state, v5, productId: product.id, selections });
    if (result.ok && result.line) onAdd(result.line);
  }

  return <div className="rcd-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="rcd-modal rcd-customer-config-modal" role="dialog" aria-modal="true" aria-labelledby="rcd-config-title">
      <header><div><span className="rcd-eyebrow">Personalizá tu plato</span><h2 id="rcd-config-title">{product.name}</h2><p>{product.description}</p></div><button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
      {groups.map((group) => <fieldset key={group.id}><legend>{group.name}<small>{group.required ? "Elegí una opción" : "Opcional"}</small></legend><div className="rcd-option-grid">{group.options.map((option) => { const active = selections[group.id]?.includes(option.id) ?? false; return <button type="button" className={active ? "active" : ""} aria-pressed={active} key={option.id} onClick={() => toggle(group, option.id)}><span>{active ? "✓ " : ""}{option.name}</span><strong>{option.priceDeltaMinor ? `+${formatMoney(option.priceDeltaMinor)}` : "Incluido"}</strong></button>; })}</div></fieldset>)}
      <footer><strong>{formatMoney(total)}</strong><button className="rcd-primary" type="button" disabled={!valid || !product.available} onClick={add}>Agregar al pedido</button></footer>
    </section>
  </div>;
}

function OrderPanel({ state, v5, setState, pendingProduct, clearPending }: {
  state: RestaurantDemoState;
  v5: RestaurantDemoV5State;
  setState: (state: RestaurantDemoState) => void;
  pendingProduct: RestaurantDemoProduct | null;
  clearPending: () => void;
}) {
  const [selectedProduct, setSelectedProduct] = useState<RestaurantDemoProduct | null>(null);
  const [cart, setCart] = useState<readonly RestaurantCustomerCartLine[]>([]);
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [fulfillment, setFulfillment] = useState<RestaurantCustomerFulfillment>("pickup");
  const [address, setAddress] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intentRef = useRef<string | null>(null);
  const total = restaurantCustomerCartTotalMinor(cart);

  useEffect(() => {
    if (!pendingProduct) return;
    choose(pendingProduct);
    clearPending();
  }, [pendingProduct]);

  function choose(product: RestaurantDemoProduct) {
    if (!product.available) return;
    const groups = modifierGroupsForProduct(v5, product.id);
    if (groups.length) { setSelectedProduct(product); return; }
    const result = buildRestaurantCustomerCartLine({ state, v5, productId: product.id, selections: {} });
    if (result.ok && result.line) setCart((current) => addRestaurantCustomerCartLine(current, result.line!));
  }

  function confirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null); setNotice(null);
    const idempotencyKey = intentRef.current ?? intentKey();
    intentRef.current = idempotencyKey;
    try {
      const result = commitRestaurantCustomerOrder({ state, v5, idempotencyKey, customerName, phone, fulfillment, deliveryAddress: address, cart });
      setState(result.state); setCart([]); setAddress(""); intentRef.current = null;
      setNotice(`✓ Pedido confirmado. Ya lo recibió la cocina.`);
    } catch (cause) {
      intentRef.current = null;
      const code = cause instanceof Error ? cause.message : "customer_order_failed";
      setError(code === "product_unavailable" ? "Un producto dejó de estar disponible. Actualizá el pedido." : code === "delivery_address_required" ? "Ingresá una dirección para delivery." : "No pudimos confirmar el pedido. Revisá los datos e intentá de nuevo.");
    }
  }

  return <>
    {notice ? <div className="rcd-notice" role="status">{notice}</div> : null}{error ? <div className="rcd-error" role="alert">{error}</div> : null}
    <div className="rcd-order-layout rcd-landing-order-layout">
      <div className="rcd-menu-product-zone"><MenuCatalog state={state} v5={v5} actionLabel="Pedir" onProductAction={choose} /></div>
      <aside className="rcd-cart rcd-landing-cart"><header><div><small>Tu pedido</small><strong>{cart.reduce((sum, line) => sum + line.quantity, 0)} ítems</strong></div><strong>{formatMoney(total)}</strong></header><div className="rcd-cart-lines">{cart.length ? cart.map((line) => <div className="rcd-cart-line" key={line.key}><div><strong>{line.name}</strong><small>{(line.modifiers ?? []).map((modifier) => modifier.label).join(" · ") || "Sin extras"}</small></div><div><button type="button" aria-label={`Quitar una unidad de ${line.name}`} onClick={() => { intentRef.current = null; setCart((current) => changeRestaurantCustomerCartQuantity(current, line.key, -1)); }}>−</button><span>{line.quantity}</span><button type="button" aria-label={`Agregar una unidad de ${line.name}`} onClick={() => { intentRef.current = null; setCart((current) => changeRestaurantCustomerCartQuantity(current, line.key, 1)); }}>+</button></div></div>) : <p>Elegí algo del menú para empezar.</p>}</div>
        <form className="rcd-form" onSubmit={confirm}><fieldset><legend>¿Cómo lo recibís?</legend><div className="rcd-segmented"><button type="button" className={fulfillment === "pickup" ? "active" : ""} onClick={() => { intentRef.current = null; setFulfillment("pickup"); }}>Retiro</button><button type="button" className={fulfillment === "delivery" ? "active" : ""} onClick={() => { intentRef.current = null; setFulfillment("delivery"); }}>Delivery</button></div></fieldset><label>Nombre<input required value={customerName} onChange={(event) => { intentRef.current = null; setCustomerName(event.target.value); }} /></label><label>Teléfono <small>opcional</small><input value={phone} onChange={(event) => { intentRef.current = null; setPhone(event.target.value); }} /></label>{fulfillment === "delivery" ? <label>Dirección<input required value={address} onChange={(event) => { intentRef.current = null; setAddress(event.target.value); }} /></label> : null}<button className="rcd-primary" type="submit" disabled={!cart.length || !customerName.trim()}>Confirmar pedido · {formatMoney(total)}</button></form>
      </aside>
    </div>
    {selectedProduct ? <Configurator product={selectedProduct} state={state} v5={v5} onClose={() => setSelectedProduct(null)} onAdd={(line) => { intentRef.current = null; setCart((current) => addRestaurantCustomerCartLine(current, line)); setSelectedProduct(null); }} /> : null}
  </>;
}

function ReservationPanel({ state, setState }: { state: RestaurantDemoState; setState: (state: RestaurantDemoState) => void }) {
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [partySize, setPartySize] = useState(2);
  const [date, setDate] = useState(addLocalDays(localDateString(), 1));
  const [time, setTime] = useState("21:00");
  const [area, setArea] = useState("all");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intentRef = useRef<string | null>(null);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError(null); setNotice(null);
    const idempotencyKey = intentRef.current ?? intentKey(); intentRef.current = idempotencyKey;
    try {
      const result = commitRestaurantCustomerReservation({ state, idempotencyKey, customerName, phone, partySize, date, time, area: area === "all" ? null : area });
      setState(result.state); intentRef.current = null;
      setNotice(`✓ Reserva confirmada para el ${date} a las ${time}. Te esperamos.`);
    } catch (cause) {
      intentRef.current = null;
      const code = cause instanceof Error ? cause.message : "reservation_failed";
      setError(code === "reservation_unavailable" ? "No hay una mesa compatible en ese horario. Probá otra hora." : "No pudimos confirmar la reserva. Revisá los datos e intentá de nuevo.");
    }
  }

  return <div className="rcd-reservation-layout rcd-landing-reservation-layout">{notice ? <div className="rcd-notice" role="status">{notice}</div> : null}{error ? <div className="rcd-error" role="alert">{error}</div> : null}<form className="rcd-form rcd-reservation-form" onSubmit={submit}><label>Nombre<input required value={customerName} onChange={(event) => { intentRef.current = null; setCustomerName(event.target.value); }} /></label><label>Teléfono <small>opcional</small><input value={phone} onChange={(event) => { intentRef.current = null; setPhone(event.target.value); }} /></label><label>Personas<input type="number" min="1" max="30" required value={partySize} onChange={(event) => { intentRef.current = null; setPartySize(Number(event.target.value)); }} /></label><label>Fecha<input type="date" min={localDateString()} required value={date} onChange={(event) => { intentRef.current = null; setDate(event.target.value); }} /></label><label>Hora<input type="time" required value={time} onChange={(event) => { intentRef.current = null; setTime(event.target.value); }} /></label><label>Sector<select value={area} onChange={(event) => { intentRef.current = null; setArea(event.target.value); }}><option value="all">Cualquier sector</option>{state.areas.filter((item) => item.active).map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}</select></label><button className="rcd-primary" type="submit">Confirmar reserva</button></form><aside className="rcd-reservation-card rcd-hospitality-card"><span className="rcd-eyebrow">Nos vemos en Casa Nativa</span><strong>Una mesa, sin vueltas.</strong><p>Reservá para almuerzos, cenas, encuentros o celebraciones. Podés elegir salón o patio según disponibilidad.</p><div><span>Salón interior</span><span>Patio</span><span>Mesas para grupos</span></div><small>Para grupos de más de 8 personas, contactanos directamente.</small></aside></div>;
}

function MenuCatalog({ state, v5, actionLabel, onProductAction }: {
  state: RestaurantDemoState;
  v5: RestaurantDemoV5State;
  actionLabel?: string;
  onProductAction?: (product: RestaurantDemoProduct) => void;
}) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const products = state.products.filter((product) => product.active)
    .filter((product) => category === "all" || product.category === category)
    .filter((product) => !search || normalized(`${product.name} ${product.description} ${product.tags.join(" ")}`).includes(normalized(search)))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const categories = state.categories.filter((item) => item.active && state.products.some((product) => product.active && product.category === item.name)).sort((a, b) => a.sortOrder - b.sortOrder);
  return <>
    <div className="rcd-menu-toolbar rcd-landing-menu-toolbar"><input type="search" aria-label="Buscar en el menú" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar en el menú…" /><div><button type="button" className={category === "all" ? "active" : ""} onClick={() => setCategory("all")}>Todo</button>{categories.map((item) => <button type="button" className={category === item.name ? "active" : ""} key={item.id} onClick={() => setCategory(item.name)}>{item.name}</button>)}</div></div>
    <div className="rcd-product-grid rcd-landing-product-grid">{products.map((product) => <ProductCard key={product.id} product={product} v5={v5} actionLabel={actionLabel} onAction={onProductAction ? () => onProductAction(product) : undefined} />)}</div>
  </>;
}

function MenuExperience({ state, v5, setState, initialMode }: {
  state: RestaurantDemoState;
  v5: RestaurantDemoV5State;
  setState: (state: RestaurantDemoState) => void;
  initialMode: MenuMode;
}) {
  const [mode, setMode] = useState<MenuMode>(initialMode);
  const [pendingProduct, setPendingProduct] = useState<RestaurantDemoProduct | null>(null);

  function orderProduct(product: RestaurantDemoProduct) {
    setPendingProduct(product);
    setMode("order");
  }

  return <section className="rcd-section rcd-menu-hub">
    <header className="rcd-menu-hub-heading"><div><span className="rcd-eyebrow">Casa Nativa</span><h2>Menú</h2></div><p>Consultá la carta, hacé tu pedido o reservá tu mesa desde el mismo lugar.</p></header>
    <div className="rcd-menu-mode-tabs" role="tablist" aria-label="Opciones del menú"><button type="button" className={mode === "menu" ? "active" : ""} onClick={() => setMode("menu")}>Carta</button><button type="button" className={mode === "order" ? "active" : ""} onClick={() => setMode("order")}>Pedir</button><button type="button" className={mode === "reservation" ? "active" : ""} onClick={() => setMode("reservation")}>Reservar</button></div>
    {mode === "menu" ? <MenuCatalog state={state} v5={v5} actionLabel="Pedir" onProductAction={orderProduct} /> : null}
    {mode === "order" ? <OrderPanel state={state} v5={v5} setState={setState} pendingProduct={pendingProduct} clearPending={() => setPendingProduct(null)} /> : null}
    {mode === "reservation" ? <ReservationPanel state={state} setState={setState} /> : null}
  </section>;
}

export function RestaurantCommercialCustomer({ businessSlug, storageSlug, businessName, surface, navigate }: {
  businessSlug: string;
  storageSlug: string;
  businessName: string;
  surface: RestaurantCommercialCustomerSurface;
  navigate: (path: string) => void;
}) {
  const initialState = useMemo(() => ensureRestaurantCommercialCatalog(storageSlug, businessName), [storageSlug, businessName]);
  const [state, setRawState] = useState<RestaurantDemoState>(initialState);
  const [v5] = useState<RestaurantDemoV5State>(() => ensureRestaurantCommercialV5(storageSlug, initialState));
  const menuRef = useRef<HTMLElement | null>(null);
  const initialMode: MenuMode = surface === "order" ? "order" : surface === "reservation" ? "reservation" : "menu";

  function setState(next: RestaurantDemoState) {
    const normalizedState = Object.freeze({ ...next, businessName });
    saveRestaurantDemoState(storageSlug, normalizedState);
    setRawState(normalizedState);
  }

  function scrollToMenu() {
    menuRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  useEffect(() => {
    if (surface === "home") return;
    window.setTimeout(scrollToMenu, 40);
  }, []);

  const featured = state.products.filter((product) => product.active && product.featured).slice(0, 3);

  return <div className="rcd-public-shell rcd-restaurant-landing">
    <Header businessSlug={businessSlug} businessName={businessName} onMenu={scrollToMenu} navigate={navigate} />
    <main className="rcd-public-main rcd-landing-main">
      <section className="rcd-landing-hero">
        <div className="rcd-landing-hero-copy"><span className="rcd-eyebrow">Cocina casera contemporánea</span><h1>Cocina para compartir, momentos para volver.</h1><p>Sabores conocidos, ingredientes frescos y una cocina hecha para disfrutar sin apuro.</p><div className="rcd-actions"><button className="rcd-primary" type="button" onClick={scrollToMenu}>Ver menú</button><button className="rcd-secondary" type="button" onClick={scrollToMenu}>Reservar una mesa</button></div><div className="rcd-landing-facts"><span>Hoy · 12:00–00:00</span><span>Av. del Lago 482 · Villa Carlos Paz</span><span>Salón · Patio · Take away</span></div></div>
        <div className="rcd-landing-hero-image"><img src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1400&q=86" alt="Salón de Casa Nativa" /><div><small>Casa Nativa</small><strong>Una mesa, buena comida y tiempo para compartir.</strong></div></div>
      </section>

      <section className="rcd-featured-strip"><div><span className="rcd-eyebrow">Favoritos de la casa</span><strong>Los platos que siempre vuelven a la mesa.</strong></div>{featured.map((product) => <button type="button" key={product.id} onClick={scrollToMenu}><span>{product.name}</span><strong>{formatMoney(product.priceMinor)}</strong></button>)}</section>

      <div ref={menuRef as React.RefObject<HTMLDivElement>} className="rcd-menu-anchor"><MenuExperience state={state} v5={v5} setState={setState} initialMode={initialMode} /></div>

      <section className="rcd-landing-info"><div><span className="rcd-eyebrow">Casa Nativa</span><h2>Una cocina simple, bien hecha.</h2><p>Recetas de siempre, ingredientes frescos y platos pensados para compartir. Vení a almorzar, cenar o quedarte un rato más.</p></div><div><strong>Horarios</strong><span>Martes a jueves · 12:00–00:00</span><span>Viernes y sábado · 12:00–01:00</span><span>Domingo · 12:00–00:00</span><span>Lunes · cerrado</span></div><div><strong>Encontranos</strong><span>Av. del Lago 482</span><span>Villa Carlos Paz, Córdoba</span><span>+54 9 351 555 2840</span><span>@casanativa</span></div></section>

      <footer className="rcd-footer rcd-landing-footer"><div><strong>{businessName}</strong><span>Cocina para compartir, momentos para volver.</span></div><div><span>Av. del Lago 482 · Villa Carlos Paz</span><span>+54 9 351 555 2840</span></div><small>Powered by ClickDialog</small></footer>
    </main>
  </div>;
}
